import { notifyInfo } from 'api';
import { WORDS } from 'utils/wordle.wordlist.utils';
import { default as GraphemeSplitter } from 'grapheme-splitter';

export type GameStats = {
    winDistribution: number[];
    gamesFailed: number;
    currentStreak: number;
    bestStreak: number;
    totalGames: number;
    successRate: number;
};

export type StoredGameState = {
    guesses: string[];
    solution: string;
};

export type CharStatus = 'absent' | 'present' | 'correct';

// Constants Settings

export const MAX_CHALLENGES = 6;
export const REVEAL_TIME_MS = 350;
export const VALID_GUESSES = ['banco', 'fondo', 'valor', 'mixto', 'renta'];

// Words Related

export const isWordInWordList = (word: string) => {
    return (
        WORDS.includes(localeAwareLowerCase(word)) ||
        VALID_GUESSES.includes(localeAwareLowerCase(word))
    );
};

// build a set of previously revealed letters - present and correct
// guess must use correct letters in that space and any other revealed letters
// also check if all revealed instances of a letter are used (i.e. two C's)
// export const findFirstUnusedReveal = (word: string, guesses: string[]) => {
//     if (guesses.length === 0) {
//         return false;
//     }

//     const lettersLeftArray = new Array<string>();
//     const guess = guesses[guesses.length - 1];
//     const statuses = getGuessStatuses(solution, guess);
//     const splitWord = unicodeSplit(word);
//     const splitGuess = unicodeSplit(guess);

//     for (let i = 0; i < splitGuess.length; i++) {
//         if (statuses[i] === 'correct' || statuses[i] === 'present') {
//             lettersLeftArray.push(splitGuess[i]);
//         }
//         if (statuses[i] === 'correct' && splitWord[i] !== splitGuess[i]) {
//             return WRONG_SPOT_MESSAGE(splitGuess[i], i + 1);
//         }
//     }

//     // check for the first unused letter, taking duplicate letters
//     // into account - see issue #198
//     let n;
//     for (const letter of splitWord) {
//         n = lettersLeftArray.indexOf(letter);
//         if (n !== -1) {
//             lettersLeftArray.splice(n, 1);
//         }
//     }

//     if (lettersLeftArray.length > 0) {
//         return NOT_CONTAINED_MESSAGE(lettersLeftArray[0]);
//     }
//     return false;
// };

export const unicodeSplit = (word: string) => {
    return new GraphemeSplitter().splitGraphemes(word);
};

export const unicodeLength = (word: string) => {
    return unicodeSplit(word).length;
};

export const localeAwareLowerCase = (text: string) => {
    return process.env.REACT_APP_LOCALE_STRING
        ? text.toLocaleLowerCase(process.env.REACT_APP_LOCALE_STRING)
        : text.toLowerCase();
};

export const localeAwareUpperCase = (text: string) => {
    return process.env.REACT_APP_LOCALE_STRING
        ? text.toLocaleUpperCase(process.env.REACT_APP_LOCALE_STRING)
        : text.toUpperCase();
};

// LocalStorage Related

export const saveGameStateToLocalStorage = (gameState: StoredGameState) => {
    localStorage.setItem('gameState', JSON.stringify(gameState));
};

export const loadGameStateFromLocalStorage = () => {
    const state = localStorage.getItem('gameState');
    return state ? (JSON.parse(state) as StoredGameState) : null;
};

// Statuses Related

export const getStatuses = (
    solution: string,
    guesses: string[]
): { [key: string]: CharStatus } => {
    const charObj: { [key: string]: CharStatus } = {};
    const splitSolution = unicodeSplit(solution);

    guesses.forEach(word => {
        unicodeSplit(word).forEach((letter, i) => {
            if (!splitSolution.includes(letter)) {
                // make status absent
                return (charObj[letter] = 'absent');
            }

            if (letter === splitSolution[i]) {
                //make status correct
                return (charObj[letter] = 'correct');
            }

            if (charObj[letter] !== 'correct') {
                //make status present
                return (charObj[letter] = 'present');
            }
        });
    });

    return charObj;
};

export const getGuessStatuses = (
    solution: string,
    guess: string
): CharStatus[] => {
    const splitSolution = unicodeSplit(solution);
    const splitGuess = unicodeSplit(guess);

    const solutionCharsTaken = splitSolution.map(_ => false);

    const statuses: CharStatus[] = Array.from(Array(guess.length));

    // handle all correct cases first
    splitGuess.forEach((letter, i) => {
        if (letter === splitSolution[i]) {
            statuses[i] = 'correct';
            solutionCharsTaken[i] = true;
            return;
        }
    });

    splitGuess.forEach((letter, i) => {
        if (statuses[i]) return;

        if (!splitSolution.includes(letter)) {
            // handles the absent case
            statuses[i] = 'absent';
            return;
        }

        // now we are left with "present"s
        const indexOfPresentChar = splitSolution.findIndex(
            (x, index) => x === letter && !solutionCharsTaken[index]
        );

        if (indexOfPresentChar > -1) {
            statuses[i] = 'present';
            solutionCharsTaken[indexOfPresentChar] = true;
            return;
        } else {
            statuses[i] = 'absent';
            return;
        }
    });

    return statuses;
};
